// src/api/services/http.service.ts

/**
 * HTTP Service - FIXED
 *
 * KEY FIXES:
 * 1. No double unwrapping of response.data
 * 2. Clear data extraction from API response format
 * 3. Proper generic typing
 *
 * DATA FLOW (FIXED):
 * ┌─────────────────────────────────────────────────────────────┐
 * │                                                             │
 * │  Backend sends: { success: true, data: { user: {...} } }    │
 * │       ↓                                                     │
 * │  Axios wraps in: { data: { success: true, data: {...} } }   │
 * │       ↓                                                     │
 * │  responseInterceptor: returns full AxiosResponse ✅          │
 * │       ↓                                                     │
 * │  HttpService: response.data                                 │
 * │  → { success: true, data: { user: {...} } } ✅              │
 * │       ↓                                                     │
 * │  Returns the full API response body                         │
 * │  Caller can access .data to get { user: {...} }             │
 * │                                                             │
 * └─────────────────────────────────────────────────────────────┘
 */

import { axiosClient } from "../client";
import type { IHttpService, RequestOptions } from "./types";
import { AxiosErrorHandler } from "@/core/errors/handlers";
import type { ErrorContext } from "@/core/errors/types";
import { logger } from "@/core/utils";

class HttpService implements IHttpService {
  private abortControllers: Map<string, AbortController> = new Map();

  /**
   * Create abort signal for request cancellation
   */
  private getAbortSignal(requestId?: string): AbortSignal | undefined {
    if (!requestId) return undefined;

    // Cancel existing request with same ID
    this.cancelRequest(requestId);

    const controller = new AbortController();
    this.abortControllers.set(requestId, controller);
    return controller.signal;
  }

  cancelRequest(requestId: string): void {
    const controller = this.abortControllers.get(requestId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(requestId);
    }
  }

  cancelAllRequests(): void {
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
  }

  /**
   * Core request method - THE SINGLE PLACE where data unwrapping happens
   *
   * TYPE PARAMETER T:
   * T represents the FULL API response body shape.
   *
   * If your API returns: { success: true, data: { users: [...] } }
   * Then T = ApiSuccessResponse<{ users: User[] }>
   *
   * The caller gets back the full response body and can access .data
   */
  private async request<T>(
    method: string,
    url: string,
    options: RequestOptions = {},
    data?: unknown,
  ): Promise<T> {
    const { requestId, skipErrorHandling = false, ...axiosConfig } = options;
    const signal = this.getAbortSignal(requestId);

    const errorContext: Partial<ErrorContext> = {
      method: method.toUpperCase(),
      url,
      action: `HTTP ${method.toUpperCase()}`,
    };

    try {
      const response = await axiosClient.request({
        method,
        url,
        data,
        signal,
        ...axiosConfig,
      });

      // Cleanup abort controller
      if (requestId) {
        this.abortControllers.delete(requestId);
      }

      /**
       * ✅ THE FIX: Single unwrap
       *
       * response.data IS your API response body:
       * { success: true, data: { user: {...} }, message: "..." }
       *
       * We return this full body. The service layer (userService, etc.)
       * knows the shape and extracts what it needs.
       */
      return response.data.data as T;
    } catch (error) {
      // Cleanup abort controller
      if (requestId) {
        this.abortControllers.delete(requestId);
      }

      // Skip error handling if requested (caller will handle it)
      if (skipErrorHandling) {
        throw error;
      }

      logger.error("HTTP Request failed", {
        method,
        url,
        error: error instanceof Error ? error.message : "Unknown error",
        status: (error as any)?.response?.status,
      });

      // Transform to AppError and throw
      throw AxiosErrorHandler.handle(error, { context: errorContext });
    }
  }

  // ─── HTTP Methods ──────────────────────────────────

  async get<T = unknown>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("GET", url, options);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>("POST", url, options, data);
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>("PUT", url, options, data);
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>("PATCH", url, options, data);
  }

  async delete<T = unknown>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("DELETE", url, options);
  }

  // ─── File Operations ──────────────────────────────

  async upload<T = unknown>(
    url: string,
    file: File | FormData,
    options?: RequestOptions & {
      onProgress?: (percent: number) => void;
    },
  ): Promise<T> {
    const { onProgress, ...restOptions } = options ?? {};

    const formData =
      file instanceof FormData
        ? file
        : (() => {
            const fd = new FormData();
            fd.append("file", file);
            return fd;
          })();

    return this.post<T>(url, formData, {
      ...restOptions,
      headers: {
        "Content-Type": "multipart/form-data",
        ...restOptions?.headers,
      },
      onUploadProgress: onProgress
        ? (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              onProgress(percent);
            }
          }
        : undefined,
    });
  }

  async download(
    url: string,
    filename: string,
    options?: RequestOptions,
  ): Promise<void> {
    const response = await axiosClient.get(url, {
      ...options,
      responseType: "blob",
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

export const httpService = new HttpService();
